import React from 'react'
import {
    Stepper,
    Step,
    StepLabel,
    StepConnector,
    Typography,
    Box,
    Tooltip,
    Button,
    CircularProgress,
} from '@material-ui/core'
import { makeStyles, Theme, withStyles } from '@material-ui/core/styles'
import {
    Check as CheckIcon,
    Create as CreateIcon,
    TableChart as RentRollIcon,
    Tune as AttributeIcon,
    CloudUpload as UploadIcon,
    HourglassEmpty as ParsingIcon,
    Assignment as ReviewIcon,
    PlayArrow as ExtractionIcon,
    Assignment as AuditIcon,
    ArrowForward as ArrowForwardIcon,
} from '@material-ui/icons'

import { LeaseAuditStep, LEASE_AUDIT_STEPS, STEP_LABELS } from '../types'

export interface StepIndicatorProps {
    currentStep: LeaseAuditStep
    latestReadyStep: LeaseAuditStep
    isProcessing?: boolean
    onStepClick?: (step: LeaseAuditStep) => void
    onNextStep?: () => void
}

const ColorlibConnector = withStyles((theme: Theme) => ({
    alternativeLabel: {
        top: 18,
    },
    active: {
        '& $line': {
            backgroundImage:
                'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
        },
    },
    completed: {
        '& $line': {
            backgroundImage:
                'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
        },
    },
    line: {
        height: 3,
        border: 0,
        backgroundColor: theme.palette.grey[300],
        borderRadius: 1,
    },
}))(StepConnector)

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        padding: theme.spacing(2, 0),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    stepper: {
        backgroundColor: 'transparent',
        padding: theme.spacing(1, 0),
        width: '100%',
        '& .MuiStepLabel-label': {
            marginTop: '8px !important',
        },
        '& .MuiStepLabel-iconContainer': {
            paddingRight: 0,
        },
    },
    stepIconRoot: {
        backgroundColor: theme.palette.grey[300],
        zIndex: 1,
        color: '#fff',
        width: 40,
        height: 40,
        display: 'flex',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
    },
    stepIconActive: {
        backgroundImage:
            'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
        boxShadow: '0 4px 10px 0 rgba(59, 130, 246, 0.25)',
    },
    stepIconCompleted: {
        backgroundImage: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    },
    stepIconReadyToAdvance: {
        animation: '$pulse 2s infinite',
        cursor: 'pointer',
    },
    stepIconLocked: {
        cursor: 'not-allowed',
        opacity: 0.5,
        '&:hover': {
            transform: 'none',
            boxShadow: 'none',
        },
    },
    stepIconProcessing: {
        backgroundImage:
            'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
        boxShadow: '0 4px 10px 0 rgba(59, 130, 246, 0.25)',
    },
    processingSpinner: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
        color: '#fff',
    },
    stepLabel: {
        marginTop: theme.spacing(1),
    },
    stepLabelText: {
        fontSize: '0.7rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        maxWidth: 100,
        margin: '0 auto',
        display: 'block',
        lineHeight: 1.2,
    },
    stepLabelActive: {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    stepLabelCompleted: {
        color: theme.palette.success.main,
    },
    nextStepButton: {
        marginTop: theme.spacing(2),
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1, 3),
        animation: '$pulse 2s infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.7)',
        },
        '50%': {
            transform: 'scale(1.05)',
            boxShadow: '0 0 0 10px rgba(59, 130, 246, 0)',
        },
    },
}))

const getStepIcon = (step: LeaseAuditStep): React.ReactNode => {
    const iconStyle = { fontSize: 20 }

    switch (step) {
        case 'CREATE':
            return <CreateIcon style={iconStyle} />
        case 'RENT_ROLL_PROCESSING':
            return <RentRollIcon style={iconStyle} />
        case 'ATTRIBUTE_SELECTION':
            return <AttributeIcon style={iconStyle} />
        case 'DOCUMENT_UPLOAD':
            return <UploadIcon style={iconStyle} />
        case 'DOCUMENT_PARSING':
            return <ParsingIcon style={iconStyle} />
        case 'DOCUMENT_REVIEW':
            return <ReviewIcon style={iconStyle} />
        case 'DATA_EXTRACTION':
            return <ExtractionIcon style={iconStyle} />
        case 'AUDITING':
            return <AuditIcon style={iconStyle} />
        default:
            return null
    }
}

interface StepIconComponentProps {
    active: boolean
    completed: boolean
    processing: boolean
    readyToAdvance: boolean
    locked: boolean
    step: LeaseAuditStep
    onClick?: () => void
    classes: ReturnType<typeof useStyles>
}

const StepIconComponent: React.FC<StepIconComponentProps> = ({
    active,
    completed,
    processing,
    readyToAdvance,
    locked,
    step,
    onClick,
    classes,
}) => {
    const getTooltipText = () => {
        if (locked) return `${STEP_LABELS[step]} (Locked)`
        if (processing) return `${STEP_LABELS[step]} (Processing...)`
        if (completed) return `${STEP_LABELS[step]} (Completed)`
        if (readyToAdvance) return `${STEP_LABELS[step]} (Click to continue)`
        return STEP_LABELS[step]
    }

    return (
        <Tooltip title={getTooltipText()} placement="top">
            <div
                className={`${classes.stepIconRoot} ${
                    active && !processing ? classes.stepIconActive : ''
                } ${completed ? classes.stepIconCompleted : ''} ${
                    processing ? classes.stepIconProcessing : ''
                } ${readyToAdvance ? classes.stepIconReadyToAdvance : ''} ${
                    locked ? classes.stepIconLocked : ''
                }`}
                onClick={locked ? undefined : onClick}
            >
                {processing ? (
                    <>
                        {getStepIcon(step)}
                        <CircularProgress
                            size={24}
                            className={classes.processingSpinner}
                        />
                    </>
                ) : completed ? (
                    <CheckIcon style={{ fontSize: 20 }} />
                ) : (
                    getStepIcon(step)
                )}
            </div>
        </Tooltip>
    )
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
    currentStep,
    latestReadyStep,
    isProcessing = false,
    onStepClick,
    onNextStep,
}) => {
    const classes = useStyles()
    const currentStepIndex = LEASE_AUDIT_STEPS.indexOf(currentStep)
    const latestReadyStepIndex = LEASE_AUDIT_STEPS.indexOf(latestReadyStep)

    // Calculate the next step after current
    const nextStepIndex = currentStepIndex + 1
    const nextStep = LEASE_AUDIT_STEPS[nextStepIndex]

    // Show next button if there's a next step and it's ready
    const showNextButton = nextStep && nextStepIndex <= latestReadyStepIndex

    return (
        <Box className={classes.root}>
            <Stepper
                alternativeLabel
                activeStep={currentStepIndex}
                connector={<ColorlibConnector />}
                className={classes.stepper}
            >
                {LEASE_AUDIT_STEPS.map((step, index) => {
                    const isActive = index === currentStepIndex
                    const isCompleted =
                        index < currentStepIndex &&
                        index <= latestReadyStepIndex
                    const isLocked = index > latestReadyStepIndex
                    const isReadyToAdvance =
                        !isActive &&
                        index === nextStepIndex &&
                        index <= latestReadyStepIndex
                    const isProcessingStep = isActive && isProcessing

                    return (
                        <Step key={step}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <StepIconComponent
                                        active={isActive}
                                        completed={isCompleted}
                                        processing={isProcessingStep}
                                        readyToAdvance={isReadyToAdvance}
                                        locked={isLocked}
                                        step={step}
                                        onClick={() =>
                                            !isLocked && onStepClick?.(step)
                                        }
                                        classes={classes}
                                    />
                                )}
                            >
                                <Box
                                    className={`${classes.stepLabelText} ${
                                        isActive ? classes.stepLabelActive : ''
                                    } ${
                                        isCompleted
                                            ? classes.stepLabelCompleted
                                            : ''
                                    }`}
                                >
                                    {STEP_LABELS[step]}
                                </Box>
                            </StepLabel>
                        </Step>
                    )
                })}
            </Stepper>

            {/* Next Step Button
            {showNextButton && onNextStep && (
                <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    className={classes.nextStepButton}
                    endIcon={<ArrowForwardIcon />}
                    onClick={onNextStep}
                >
                    Continue to {STEP_LABELS[nextStep]}
                </Button>
            )} */}
        </Box>
    )
}
